import { useState } from 'react';
import { signIn, signUp, confirmSignUp } from 'aws-amplify/auth';

export function AuthPanel({ onSignedIn }) {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function handleSignIn(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      await signIn({ username: email, password });
      onSignedIn && onSignedIn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      setStatus('Check your email for a confirmation code.');
      setMode('confirm');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      setStatus('Confirmed. Sign in now.');
      setMode('signin');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const tabClass = (active) =>
    `text-sm font-medium pb-2 border-b-2 transition-colors ${
      active
        ? 'text-f1-text border-f1-red'
        : 'text-f1-muted border-transparent hover:text-f1-text'
    }`;

  const inputClass =
    'w-full rounded-lg bg-f1-surface2 border border-f1-border px-3 py-2.5 text-sm placeholder-f1-muted focus:outline-none focus:border-f1-red transition-colors';

  return (
    <div className="w-[360px] max-w-full rounded-2xl border border-f1-border bg-f1-surface p-6">
      <h2 className="text-lg font-semibold mb-1">Welcome</h2>
      <p className="text-xs text-f1-muted mb-5">
        Sign in to unlock the full race archive for $1.
      </p>

      {mode !== 'confirm' && (
        <div className="flex items-center gap-5 mb-5">
          <button
            type="button"
            className={tabClass(mode === 'signin')}
            onClick={() => {
              setMode('signin');
              setError(null);
            }}
          >
            Sign in
          </button>
          <button
            type="button"
            className={tabClass(mode === 'signup')}
            onClick={() => {
              setMode('signup');
              setError(null);
            }}
          >
            Sign up
          </button>
        </div>
      )}

      {(mode === 'signin' || mode === 'signup') && (
        <form
          onSubmit={mode === 'signin' ? handleSignIn : handleSignUp}
          className="flex flex-col gap-3"
        >
          <input
            className={inputClass}
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            className={inputClass}
            type="password"
            placeholder="Password"
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-f1-red px-4 py-2.5 text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {busy ? '…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>
      )}

      {mode === 'confirm' && (
        <form onSubmit={handleConfirm} className="flex flex-col gap-3">
          <input
            className={inputClass}
            type="text"
            inputMode="numeric"
            placeholder="Confirmation code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-f1-red px-4 py-2.5 text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {busy ? '…' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('signin');
              setError(null);
              setStatus(null);
            }}
            className="text-xs text-f1-muted hover:text-f1-text"
          >
            Back to sign in
          </button>
        </form>
      )}

      {error && (
        <div className="mt-4 rounded-md border border-f1-red/50 bg-f1-red/10 text-sm px-3 py-2">
          {error}
        </div>
      )}
      {status && !error && (
        <div className="mt-4 text-xs text-f1-muted">{status}</div>
      )}
    </div>
  );
}
